import * as Sentry from '@sentry/react';
import Lottie from 'lottie-react';
import { Suspense, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { toast } from 'sonner';
import celebrateAnimation from '@/assets/lottie/celebrate.json';
import { useAuth } from '@/entities/user';
import { MESSAGE, MINUTE } from '@/shared/consts';
import { useBoundStore } from '@/shared/store';
import { LoadingSpinner, Modal } from '@/shared/ui';

const SIGNUP_MODAL_STORAGE_PREFIX = 'mantang-signup-modal-shown';
const LOGIN_PENDING_STORAGE_KEY = 'mantang-login-pending';
const NEW_SIGNUP_THRESHOLD_MS = MINUTE;

const AppLayout = () => {
	const navigate = useNavigate();
	const { isLoggedIn, userId, createdAt, lastSignInAt } = useAuth();
	const { isModalOpen, modalConfig, openModal, closeModal } = useBoundStore();

	useEffect(() => {
		if (!isLoggedIn) return;
		if (sessionStorage.getItem(LOGIN_PENDING_STORAGE_KEY) !== 'true') return;

		toast.success(MESSAGE.SUCCESS.LOGIN);
		sessionStorage.removeItem(LOGIN_PENDING_STORAGE_KEY);
	}, [isLoggedIn]);

	useEffect(() => {
		if (!isLoggedIn || !userId || !createdAt || !lastSignInAt) return;

		const storageKey = `${SIGNUP_MODAL_STORAGE_PREFIX}:${userId}`;
		const hasSeenSignupModal = localStorage.getItem(storageKey) === 'true';
		if (hasSeenSignupModal) return;

		const createdTime = new Date(createdAt).getTime();
		const lastSignInTime = new Date(lastSignInAt).getTime();
		const isNewSignup =
			Number.isFinite(createdTime) &&
			Number.isFinite(lastSignInTime) &&
			Math.abs(lastSignInTime - createdTime) <= NEW_SIGNUP_THRESHOLD_MS;

		if (isNewSignup) {
			openModal({
				title: '회원가입이 완료되었어요',
				visual: (
					<Lottie
						animationData={celebrateAnimation}
						loop={false}
						className="mx-auto h-[120px]"
					/>
				),
				children: (
					<p className="whitespace-pre-line text-center">
						{'차량을 등록하면 주유소 절약 금액을\n더 정확히 계산해드려요'}
					</p>
				),
				primaryAction: {
					label: '차량 등록하기',
					onClick: () => {
						closeModal();
						navigate('/profile/regist-car');
					},
				},
				secondaryAction: {
					label: '나중에 할게요',
					onClick: closeModal,
				},
			});
			localStorage.setItem(storageKey, 'true');
		}
	}, [
		createdAt,
		isLoggedIn,
		lastSignInAt,
		userId,
		openModal,
		closeModal,
		navigate,
	]);

	return (
		<div className="relative mx-auto h-dvh max-w-md touch-none font-pretendard shadow-md">
			<Suspense fallback={<LoadingSpinner />}>
				<Outlet />
			</Suspense>
			<Modal isOpen={isModalOpen} onClose={closeModal} {...modalConfig} />
		</div>
	);
};

export default Sentry.withProfiler(AppLayout);
